import { useEffect, useCallback } from "react";
import { useCartData } from "@/zustand/cartStore";
import { useProductData } from "@/zustand/productStore";

export const useAxonTracking = () => {
  const { selectedProduct } = useCartData();
  const { currency_code, products } = useProductData();

  const fireEvent = useCallback(
    (eventName, extraData = {}) => {
      if (!window.axon || !selectedProduct) return;

      const qty = parseInt(selectedProduct.amount) || 1;
      const unitPrice = parseFloat(selectedProduct.price) || 0;
      const totalValue = unitPrice * qty;
      const matchedProduct = products?.find(
        (p) =>
          p.konnektive_id?.trim() === selectedProduct.konnektive_id ||
          p.sku?.trim() === selectedProduct.sku,
      );
      const itemImage =
        matchedProduct?.image?.url ||
        matchedProduct?.image ||
        selectedProduct.image?.url ||
        selectedProduct.image ||
        null;

      const payload = {
        currency: currency_code || "USD",
        value: totalValue,
        shipping: parseFloat(selectedProduct.shipping_price) || 0,
        tax: 0,
        affiliation: window.affiliate || null,
        items: [
          {
            item_id:
              selectedProduct.konnektive_id || selectedProduct.sku || "unknown",
            item_name: selectedProduct.display_name || "Product",
            price: unitPrice,
            quantity: qty,
            item_category: "Home & Garden",
            image_url: itemImage,
          },
        ],
        ...extraData,
      };

      window.axon("track", eventName, payload);
    },
    [selectedProduct, currency_code, products],
  );

  useEffect(() => {
    let attempt = 0;
    const fire = () => {
      if (!window.axon) {
        if (attempt++ < 20) setTimeout(fire, 250);
        return;
      }
      window.axon("track", "page_view");
    };
    fire();
  }, []);

  useEffect(() => {
    if (selectedProduct?.sku || selectedProduct?.konnektive_id) {
      fireEvent("begin_checkout");
    }
  }, [selectedProduct?.sku, selectedProduct?.konnektive_id, fireEvent]);

  const trackPurchase = async (orderId, formData) => {
    try {
      if (!window.axon || !selectedProduct) return;

      const email = formData?.email || null;
      const phone = formData?.phone || null;

      if (email || phone) {
        try {
          sessionStorage.setItem(
            "axon_user_data",
            JSON.stringify({ email, phone }),
          );
        } catch {}
      }

      fireEvent("purchase", {
        transaction_id: orderId,
        user_data: { email, phone, user_id: orderId },
      });

      // Wait for the Axon beacon to dispatch before the page navigates away.
      await new Promise((resolve) => setTimeout(resolve, 400));
    } catch (e) {}
  };

  return { trackPurchase };
};
